import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Users,
  FileText,
  Search,
  Filter,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Eye,
  X,
  CheckCircle,
  Clock,
  AlertCircle,
  DollarSign,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  Award,
  Calendar,
  RefreshCw,
  Download
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_COLORS = {
  pending_payment: "bg-yellow-100 text-yellow-800",
  submitted: "bg-blue-100 text-blue-800",
  under_review: "bg-purple-100 text-purple-800",
  selected: "bg-green-100 text-green-800",
  not_selected: "bg-gray-100 text-gray-800",
  approved: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800"
};

const STATUS_ICONS = {
  pending_payment: DollarSign,
  submitted: FileText,
  under_review: Clock,
  selected: CheckCircle,
  not_selected: AlertCircle,
  approved: CheckCircle,
  rejected: AlertCircle
};

const CATEGORIES = [
  "Business / Entrepreneurship",
  "Music / Entertainment", 
  "Arts / Culture",
  "Education",
  "Government / Public Service",
  "Community Leadership"
];

// Stats Card Component
const StatCard = ({ title, value, icon: Icon, color }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
    <div className="flex items-center justify-between">
      <div>
        <p className="font-sans text-sm text-[#4b5563]">{title}</p>
        <p className="font-serif text-3xl font-bold text-[#132238] mt-1">{value}</p>
      </div>
      <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
    </div>
  </div>
);

// Application Detail Modal
const ApplicationModal = ({ application, onClose, onStatusChange }) => {
  const [newStatus, setNewStatus] = useState(application.status);
  const [updating, setUpdating] = useState(false);

  const handleStatusUpdate = async () => {
    setUpdating(true);
    try {
      await axios.patch(`${API}/admin/applications/${application.id}/status?status=${newStatus}`);
      onStatusChange(application.id, newStatus);
      onClose();
    } catch (error) {
      console.error("Failed to update status:", error);
    }
    setUpdating(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
          <h2 className="font-serif text-2xl text-[#132238]">Application Details</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
            <X size={24} />
          </button>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Status Update */}
          <div className="bg-gray-50 rounded-xl p-4">
            <label className="block font-sans text-sm font-medium text-[#132238] mb-2">Update Status</label>
            <div className="flex gap-3">
              <select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-lg font-sans focus:outline-none focus:ring-2 focus:ring-[#0b3d91]/20"
                data-testid="select-app-status"
              >
                <option value="pending_payment">Pending Payment</option>
                <option value="submitted">Submitted</option>
                <option value="under_review">Under Review</option>
                <option value="selected">Selected</option>
                <option value="not_selected">Not Selected</option>
              </select>
              <button
                onClick={handleStatusUpdate}
                disabled={updating || newStatus === application.status}
                className="px-6 py-2 bg-[#0b3d91] text-white font-sans font-medium rounded-lg hover:bg-[#0a3580] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                data-testid="btn-update-app-status"
              >
                {updating ? "Updating..." : "Update"}
              </button>
            </div>
          </div>

          {/* Basic Info */}
          <div>
            <h3 className="font-serif text-lg text-[#0b3d91] mb-3">Basic Information</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-[#4b5563]" />
                <div>
                  <p className="font-sans text-sm text-[#4b5563]">Full Name</p>
                  <p className="font-sans font-medium text-[#132238]">{application.basic_info?.full_name}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-[#4b5563]" />
                <div>
                  <p className="font-sans text-sm text-[#4b5563]">Email</p>
                  <p className="font-sans font-medium text-[#132238]">{application.basic_info?.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-[#4b5563]" />
                <div>
                  <p className="font-sans text-sm text-[#4b5563]">Phone</p>
                  <p className="font-sans font-medium text-[#132238]">{application.basic_info?.phone || "N/A"}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-[#4b5563]" />
                <div>
                  <p className="font-sans text-sm text-[#4b5563]">Location</p>
                  <p className="font-sans font-medium text-[#132238]">{application.basic_info?.city_country}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Category */}
          <div>
            <h3 className="font-serif text-lg text-[#0b3d91] mb-3">Category & Profile</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <Award className="w-5 h-5 text-[#4b5563]" />
                <div>
                  <p className="font-sans text-sm text-[#4b5563]">Area of Impact</p>
                  <p className="font-sans font-medium text-[#132238]">{application.category?.primary_area}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Briefcase className="w-5 h-5 text-[#4b5563]" />
                <div>
                  <p className="font-sans text-sm text-[#4b5563]">Role</p>
                  <p className="font-sans font-medium text-[#132238]">{application.category?.current_role}</p>
                </div>
              </div>
            </div>
            {application.category?.organization && (
              <p className="font-sans text-[#4b5563] mt-2">Organization: {application.category.organization}</p>
            )}
          </div>

          {/* Impact */}
          <div>
            <h3 className="font-serif text-lg text-[#0b3d91] mb-3">Impact & Story</h3>
            <div className="space-y-4">
              <div>
                <p className="font-sans text-sm font-medium text-[#4b5563] mb-1">Brief Bio</p>
                <p className="font-sans text-[#132238] bg-gray-50 p-3 rounded-lg">{application.impact?.brief_bio}</p>
              </div>
              <div>
                <p className="font-sans text-sm font-medium text-[#4b5563] mb-1">Impact Made</p>
                <p className="font-sans text-[#132238] bg-gray-50 p-3 rounded-lg">{application.impact?.impact_made}</p>
              </div>
              <div>
                <p className="font-sans text-sm font-medium text-[#4b5563] mb-1">Achievements</p>
                <p className="font-sans text-[#132238] bg-gray-50 p-3 rounded-lg whitespace-pre-wrap">{application.impact?.achievements}</p>
              </div>
            </div>
          </div>

          {/* Digital Presence */}
          {application.digital && (
            <div>
              <h3 className="font-serif text-lg text-[#0b3d91] mb-3">Digital Presence</h3>
              <div className="flex flex-wrap gap-3">
                {application.digital.website && (
                  <a href={application.digital.website} target="_blank" rel="noopener noreferrer" className="px-4 py-2 bg-gray-100 rounded-full font-sans text-sm text-[#0b3d91] hover:bg-gray-200 transition-colors">
                    Website
                  </a>
                )}
                {application.digital.linkedin && (
                  <a href={application.digital.linkedin} target="_blank" rel="noopener noreferrer" className="px-4 py-2 bg-gray-100 rounded-full font-sans text-sm text-[#0b3d91] hover:bg-gray-200 transition-colors">
                    LinkedIn
                  </a>
                )}
                {application.digital.instagram && (
                  <span className="px-4 py-2 bg-gray-100 rounded-full font-sans text-sm text-[#132238]">
                    {application.digital.instagram}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Metadata */}
          <div className="pt-4 border-t border-gray-200">
            <div className="flex flex-wrap gap-4 text-sm text-[#4b5563]">
              <span className="flex items-center gap-1">
                <Calendar size={14} /> Created: {new Date(application.created_at).toLocaleDateString()}
              </span>
              <span className="flex items-center gap-1">
                <DollarSign size={14} /> Payment: {application.payment_status || "N/A"}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Nomination Detail Modal
const NominationModal = ({ nomination, onClose, onStatusChange }) => {
  const [newStatus, setNewStatus] = useState(nomination.status);
  const [updating, setUpdating] = useState(false);

  const handleStatusUpdate = async () => {
    setUpdating(true);
    try {
      await axios.patch(`${API}/admin/nominations/${nomination.id}/status?status=${newStatus}`);
      onStatusChange(nomination.id, newStatus);
      onClose();
    } catch (error) {
      console.error("Failed to update status:", error);
    }
    setUpdating(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
          <h2 className="font-serif text-2xl text-[#132238]">Nomination Details</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
            <X size={24} />
          </button>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Status Update */}
          <div className="bg-gray-50 rounded-xl p-4">
            <label className="block font-sans text-sm font-medium text-[#132238] mb-2">Update Status</label>
            <div className="flex gap-3">
              <select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-lg font-sans focus:outline-none focus:ring-2 focus:ring-[#0b3d91]/20"
                data-testid="select-nom-status"
              >
                <option value="submitted">Submitted</option>
                <option value="under_review">Under Review</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
              <button
                onClick={handleStatusUpdate}
                disabled={updating || newStatus === nomination.status}
                className="px-6 py-2 bg-[#0b3d91] text-white font-sans font-medium rounded-lg hover:bg-[#0a3580] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                data-testid="btn-update-nom-status"
              >
                {updating ? "Updating..." : "Update"}
              </button>
            </div>
          </div>

          {/* Nominator Info */}
          <div>
            <h3 className="font-serif text-lg text-[#0b3d91] mb-3">Nominator Information</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-[#4b5563]" />
                <div>
                  <p className="font-sans text-sm text-[#4b5563]">Name</p>
                  <p className="font-sans font-medium text-[#132238]">{nomination.nominator?.nominator_name}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-[#4b5563]" />
                <div>
                  <p className="font-sans text-sm text-[#4b5563]">Email</p>
                  <p className="font-sans font-medium text-[#132238]">{nomination.nominator?.nominator_email}</p>
                </div>
              </div>
            </div>
            <p className="font-sans text-[#4b5563] mt-2">Relationship: {nomination.nominator?.relationship}</p>
          </div>

          {/* Nominee Info */}
          <div>
            <h3 className="font-serif text-lg text-[#2bb673] mb-3">Nominee Information</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-[#4b5563]" />
                <div>
                  <p className="font-sans text-sm text-[#4b5563]">Name</p>
                  <p className="font-sans font-medium text-[#132238]">{nomination.nominee?.nominee_name}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-[#4b5563]" />
                <div>
                  <p className="font-sans text-sm text-[#4b5563]">Email</p>
                  <p className="font-sans font-medium text-[#132238]">{nomination.nominee?.nominee_email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-[#4b5563]" />
                <div>
                  <p className="font-sans text-sm text-[#4b5563]">Location</p>
                  <p className="font-sans font-medium text-[#132238]">{nomination.nominee?.nominee_location}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Briefcase className="w-5 h-5 text-[#4b5563]" />
                <div>
                  <p className="font-sans text-sm text-[#4b5563]">Role</p>
                  <p className="font-sans font-medium text-[#132238]">{nomination.profile?.nominee_role}</p>
                </div>
              </div>
            </div>
            <p className="font-sans text-[#4b5563] mt-2">Area: {nomination.profile?.area_of_impact}</p>
          </div>

          {/* Why Nominated */}
          <div>
            <h3 className="font-serif text-lg text-[#0b3d91] mb-3">Why Nominated</h3>
            <div className="space-y-4">
              <div>
                <p className="font-sans text-sm font-medium text-[#4b5563] mb-1">Reason for Nomination</p>
                <p className="font-sans text-[#132238] bg-gray-50 p-3 rounded-lg">{nomination.why_nominate?.why_nominating}</p>
              </div>
              <div>
                <p className="font-sans text-sm font-medium text-[#4b5563] mb-1">Impact Made</p>
                <p className="font-sans text-[#132238] bg-gray-50 p-3 rounded-lg">{nomination.why_nominate?.nominee_impact}</p>
              </div>
              {nomination.why_nominate?.nominee_achievements && (
                <div>
                  <p className="font-sans text-sm font-medium text-[#4b5563] mb-1">Achievements</p>
                  <p className="font-sans text-[#132238] bg-gray-50 p-3 rounded-lg whitespace-pre-wrap">{nomination.why_nominate.nominee_achievements}</p>
                </div>
              )}
            </div>
          </div>

          {/* Email Status */}
          <div className="pt-4 border-t border-gray-200">
            <div className="flex flex-wrap gap-4 text-sm text-[#4b5563]">
              <span className="flex items-center gap-1">
                <Calendar size={14} /> Created: {new Date(nomination.created_at).toLocaleDateString()}
              </span>
              <span className="flex items-center gap-1">
                <Mail size={14} /> Email Sent: {nomination.email_sent ? "Yes" : "No"}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState("applications");
  const [stats, setStats] = useState(null);
  const [applications, setApplications] = useState([]);
  const [nominations, setNominations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [selectedNomination, setSelectedNomination] = useState(null);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 10;

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  };

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (statusFilter) params.append("status", statusFilter);
      if (categoryFilter) params.append("category", categoryFilter);
      params.append("limit", limit);
      params.append("skip", page * limit);
      
      const response = await axios.get(`${API}/admin/applications?${params}`);
      setApplications(response.data.applications);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Failed to fetch applications:", error);
    }
    setLoading(false);
  };

  const fetchNominations = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (statusFilter) params.append("status", statusFilter);
      if (categoryFilter) params.append("category", categoryFilter);
      params.append("limit", limit);
      params.append("skip", page * limit);
      
      const response = await axios.get(`${API}/admin/nominations?${params}`);
      setNominations(response.data.nominations);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Failed to fetch nominations:", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    setPage(0);
  }, [activeTab, search, statusFilter, categoryFilter]);

  useEffect(() => {
    if (activeTab === "applications") {
      fetchApplications();
    } else {
      fetchNominations();
    }
  }, [activeTab, search, statusFilter, categoryFilter, page]);

  const handleApplicationStatusChange = (id, newStatus) => {
    setApplications(apps => apps.map(app => 
      app.id === id ? { ...app, status: newStatus } : app
    ));
    fetchStats();
  };

  const handleNominationStatusChange = (id, newStatus) => {
    setNominations(noms => noms.map(nom => 
      nom.id === id ? { ...nom, status: newStatus } : nom
    ));
    fetchStats();
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="min-h-screen bg-[#f4f7fb]">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" data-testid="admin-logo-link">
              <img 
                src="https://customer-assets-jai6qajn.emergentagent.net/job_jambassador-landing/artifacts/91dtn44o_jambassador-standard.png" 
                alt="JamBassador Awards" 
                className="h-16 md:h-20 w-auto"
              />
            </Link>
            <div className="hidden sm:block border-l border-gray-200 pl-4">
              <h1 className="font-serif text-xl text-[#132238]">Admin Dashboard</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                fetchStats();
                if (activeTab === "applications") fetchApplications();
                else fetchNominations();
              }}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg font-sans text-sm text-[#132238] hover:bg-gray-200 transition-colors"
              data-testid="btn-refresh"
            >
              <RefreshCw size={16} /> Refresh
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Grid */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <StatCard 
              title="Total Applications" 
              value={stats.applications.total} 
              icon={FileText} 
              color="bg-[#0b3d91]" 
            />
            <StatCard 
              title="Paid Applications" 
              value={stats.payments.total_paid} 
              icon={DollarSign} 
              color="bg-[#2bb673]" 
            />
            <StatCard 
              title="Total Nominations" 
              value={stats.nominations.total} 
              icon={Users} 
              color="bg-[#f2c94c]" 
            />
            <StatCard 
              title="Selected" 
              value={stats.applications.selected} 
              icon={Award} 
              color="bg-[#8b5cf6]" 
            />
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="border-b border-gray-200">
            <div className="flex">
              <button
                onClick={() => setActiveTab("applications")}
                className={`flex-1 px-6 py-4 font-sans font-medium text-sm transition-colors ${
                  activeTab === "applications" 
                    ? "text-[#0b3d91] border-b-2 border-[#0b3d91] bg-[#0b3d91]/5" 
                    : "text-[#4b5563] hover:text-[#132238]"
                }`}
                data-testid="tab-applications"
              >
                <FileText className="w-4 h-4 inline mr-2" />
                Applications ({stats?.applications.total || 0})
              </button>
              <button
                onClick={() => setActiveTab("nominations")}
                className={`flex-1 px-6 py-4 font-sans font-medium text-sm transition-colors ${
                  activeTab === "nominations" 
                    ? "text-[#0b3d91] border-b-2 border-[#0b3d91] bg-[#0b3d91]/5" 
                    : "text-[#4b5563] hover:text-[#132238]"
                }`}
                data-testid="tab-nominations"
              >
                <Users className="w-4 h-4 inline mr-2" />
                Nominations ({stats?.nominations.total || 0})
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <div className="flex flex-wrap gap-3">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4b5563]" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name, email, role..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg font-sans text-sm focus:outline-none focus:ring-2 focus:ring-[#0b3d91]/20 focus:border-[#0b3d91]"
                  data-testid="input-search"
                />
              </div>
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4b5563]" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="pl-10 pr-8 py-2 border border-gray-200 rounded-lg font-sans text-sm focus:outline-none focus:ring-2 focus:ring-[#0b3d91]/20 appearance-none bg-white"
                  data-testid="select-status-filter"
                >
                  <option value="">All Statuses</option>
                  {activeTab === "applications" ? (
                    <>
                      <option value="pending_payment">Pending Payment</option>
                      <option value="submitted">Submitted</option>
                      <option value="under_review">Under Review</option>
                      <option value="selected">Selected</option>
                      <option value="not_selected">Not Selected</option>
                    </>
                  ) : (
                    <>
                      <option value="submitted">Submitted</option>
                      <option value="under_review">Under Review</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                    </>
                  )}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4b5563] pointer-events-none" />
              </div>
              <div className="relative">
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="pl-4 pr-8 py-2 border border-gray-200 rounded-lg font-sans text-sm focus:outline-none focus:ring-2 focus:ring-[#0b3d91]/20 appearance-none bg-white"
                  data-testid="select-category-filter"
                >
                  <option value="">All Categories</option>
                  {CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4b5563] pointer-events-none" />
              </div>
              
              {/* Export Button */}
              <a
                href={`${API}/admin/export/${activeTab === "applications" ? "applications" : "nominations"}${statusFilter || categoryFilter ? `?${statusFilter ? `status=${statusFilter}` : ""}${statusFilter && categoryFilter ? "&" : ""}${categoryFilter ? `category=${encodeURIComponent(categoryFilter)}` : ""}` : ""}`}
                download
                className="flex items-center gap-2 px-4 py-2 bg-[#2bb673] text-white rounded-lg font-sans text-sm font-medium hover:bg-[#249e63] transition-colors"
                data-testid="btn-export-csv"
              >
                <Download size={16} /> Export CSV
              </a>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            {loading ? (
              <div className="p-8 text-center">
                <RefreshCw className="w-8 h-8 text-[#0b3d91] animate-spin mx-auto mb-2" />
                <p className="font-sans text-[#4b5563]">Loading...</p>
              </div>
            ) : activeTab === "applications" ? (
              <table className="w-full" data-testid="applications-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left font-sans text-xs font-medium text-[#4b5563] uppercase tracking-wider">Applicant</th>
                    <th className="px-6 py-3 text-left font-sans text-xs font-medium text-[#4b5563] uppercase tracking-wider">Category</th>
                    <th className="px-6 py-3 text-left font-sans text-xs font-medium text-[#4b5563] uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left font-sans text-xs font-medium text-[#4b5563] uppercase tracking-wider">Payment</th>
                    <th className="px-6 py-3 text-left font-sans text-xs font-medium text-[#4b5563] uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-right font-sans text-xs font-medium text-[#4b5563] uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {applications.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-8 text-center font-sans text-[#4b5563]">
                        No applications found
                      </td>
                    </tr>
                  ) : applications.map((app) => (
                    <tr key={app.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-sans font-medium text-[#132238]">{app.basic_info?.full_name}</p>
                          <p className="font-sans text-sm text-[#4b5563]">{app.basic_info?.email}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="font-sans text-sm text-[#132238]">{app.category?.primary_area}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full font-sans text-xs font-medium ${STATUS_COLORS[app.status] || 'bg-gray-100 text-gray-800'}`}>
                          {app.status?.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full font-sans text-xs font-medium ${app.payment_status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                          {app.payment_status || 'pending'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="font-sans text-sm text-[#4b5563]">
                          {new Date(app.created_at).toLocaleDateString()}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => setSelectedApplication(app)}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                          data-testid={`btn-view-app-${app.id}`}
                        >
                          <Eye size={18} className="text-[#0b3d91]" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <table className="w-full" data-testid="nominations-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left font-sans text-xs font-medium text-[#4b5563] uppercase tracking-wider">Nominee</th>
                    <th className="px-6 py-3 text-left font-sans text-xs font-medium text-[#4b5563] uppercase tracking-wider">Nominated By</th>
                    <th className="px-6 py-3 text-left font-sans text-xs font-medium text-[#4b5563] uppercase tracking-wider">Category</th>
                    <th className="px-6 py-3 text-left font-sans text-xs font-medium text-[#4b5563] uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left font-sans text-xs font-medium text-[#4b5563] uppercase tracking-wider">Email Sent</th>
                    <th className="px-6 py-3 text-right font-sans text-xs font-medium text-[#4b5563] uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {nominations.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-8 text-center font-sans text-[#4b5563]">
                        No nominations found
                      </td>
                    </tr>
                  ) : nominations.map((nom) => (
                    <tr key={nom.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-sans font-medium text-[#132238]">{nom.nominee?.nominee_name}</p>
                          <p className="font-sans text-sm text-[#4b5563]">{nom.nominee?.nominee_email}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="font-sans text-sm text-[#132238]">{nom.nominator?.nominator_name}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="font-sans text-sm text-[#132238]">{nom.profile?.area_of_impact}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full font-sans text-xs font-medium ${STATUS_COLORS[nom.status] || 'bg-gray-100 text-gray-800'}`}>
                          {nom.status?.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full font-sans text-xs font-medium ${nom.email_sent ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {nom.email_sent ? 'Sent' : 'Not Sent'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => setSelectedNomination(nom)}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                          data-testid={`btn-view-nom-${nom.id}`}
                        >
                          <Eye size={18} className="text-[#0b3d91]" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="p-4 border-t border-gray-200 flex items-center justify-between">
              <p className="font-sans text-sm text-[#4b5563]">
                Showing {page * limit + 1} to {Math.min((page + 1) * limit, total)} of {total} results
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  data-testid="btn-prev-page"
                >
                  <ChevronLeft size={18} />
                </button>
                <span className="font-sans text-sm text-[#132238]">
                  Page {page + 1} of {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  data-testid="btn-next-page"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Modals */}
      {selectedApplication && (
        <ApplicationModal
          application={selectedApplication}
          onClose={() => setSelectedApplication(null)}
          onStatusChange={handleApplicationStatusChange}
        />
      )}
      {selectedNomination && (
        <NominationModal
          nomination={selectedNomination}
          onClose={() => setSelectedNomination(null)}
          onStatusChange={handleNominationStatusChange}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
